import { useCallback } from "react";
import { cartText } from '../api.js';

function formatPrice(priceStr, decimals = 2) {
	const num = parseInt(priceStr, 10) / 100;
	return `$${num.toFixed(decimals)}`;
}

export default function CartRow({ item, busy, onUpdateQty, onRemove, presentation = {} }) {
	const img = item.images?.[0];
	const name = cartText(item.name);
	const variation = item.variation
		?.map((v) => v.value)
		.filter(Boolean)
		.join(", ");
	const lineTotal = formatPrice(item.totals.line_total);
	const qty = item.quantity;
	const maxQty = item.quantity_limits?.maximum ?? 99;
	const minQty = item.quantity_limits?.minimum ?? 1;
	const step = item.quantity_limits?.multiple_of || 1;
	const editable = item.quantity_limits?.editable !== false;
	const compact = presentation.inlineItemData === true;
	const fields = (item.item_data || []).filter((entry) => !entry.hidden).map((entry) => {
		const key = cartText(entry.key);
		const value = cartText(entry.value);
		const field = presentation.fields?.[key] || {};
		const label = field.label == null ? key : cartText(field.label);
		// Decode each source once; decoding the assembled text would strip literal
		// angle-bracket text supplied by the shopper after its first decode.
		const mappedValue = field.valueText?.[value];
		const text = mappedValue != null ? cartText(mappedValue) : (field.format
			? cartText(field.format).replace('%s', () => value)
			: `${label ? label + ': ' : ''}${value}`);
		const role = ['eyebrow', 'note', 'warning'].includes(field.role) ? field.role : 'details';
		return { key, value, text, role };
	});
	const details = [variation && cartText(variation), ...fields.filter((field) => field.role === 'details').map((field) => field.text)].filter(Boolean);

	const decrement = useCallback(() => {
		if (!busy && editable && qty - step >= minQty) onUpdateQty(item.key, qty - step);
	}, [qty, minQty, step, busy, editable, item.key, onUpdateQty]);

	const increment = useCallback(() => {
		if (!busy && editable && qty + step <= maxQty) onUpdateQty(item.key, qty + step);
	}, [qty, maxQty, step, busy, editable, item.key, onUpdateQty]);

	const remove = useCallback(() => {
		onRemove(item.key);
	}, [item.key, onRemove]);
	const removeControl = (
		<button type="button" className="zc-row__remove" onClick={remove} disabled={busy} aria-label={`Remove ${name}`}>
			{presentation.removeLabel || '×'}
		</button>
	);

	return (
		<div className={`zc-row${busy ? " is-busy" : ""}`}>
			{/* Image */}
			<div className="zc-row__img">
				{img ? (
					<img
						src={img.thumbnail || img.src}
						alt={img.alt || name}
						loading="lazy"
					/>
				) : (
					<div className="zc-row__img-placeholder" />
				)}
			</div>

			{/* Product info */}
			<div className="zc-row__info">
				{compact && fields.filter((field) => field.role === 'eyebrow').map((field, index) => (
					<span className="zc-row__eyebrow" key={index}>{field.value}</span>
				))}
				<span className="zc-row__name">{name}</span>
				{compact ? <>
					{details.length > 0 && <span className="zc-row__options">{details.join(' · ')}</span>}
					{fields.filter((field) => field.role === 'note' || field.role === 'warning').map((field, index) => (
						<span className={`zc-row__${field.role}`} key={index}>{field.value}</span>
					))}
				</> : <>
				{variation && <span className="zc-row__variant">{variation}</span>}
				{item.item_data?.length > 0 && (
					<dl className="zc-row__item-data">
						{item.item_data.filter((entry) => !entry.hidden).map((entry, index) => (
							<div key={`${entry.key}-${index}`}>
								<dt>{cartText(entry.key)}</dt><dd>{cartText(entry.value)}</dd>
							</div>
						))}
					</dl>
				)}
				</>}
				{presentation.removeLabel && removeControl}
			</div>

			{/* Quantity */}
			<div className="zc-row__qty">
				<button
					className="zc-row__qty-btn"
					onClick={decrement}
					disabled={busy || !editable || qty - step < minQty}
					aria-label="Decrease quantity"
				>
					&ndash;
				</button>
				<span className="zc-row__qty-val">{qty}</span>
				<button
					className="zc-row__qty-btn"
					onClick={increment}
					disabled={busy || !editable || qty + step > maxQty}
					aria-label="Increase quantity"
				>
					+
				</button>
			</div>

			{/* Price */}
			<span className="zc-row__price">{lineTotal}</span>

			{/* Remove */}
			{!presentation.removeLabel && removeControl}
		</div>
	);
}
